import React from "react";
import axios from "axios";
import moment from "moment";
import XMLParser from "react-xml-parser";

import {
  amountDue,
  insuranceId,
  reservationObject,
  reservedUnitObject,
  locationCode,
  cardType,
  getPostResult,
  getInsuranceStatus,
  getGateCode,
  finishNewTenantNote,
  finalDiscountId,
  get24HourFee,
  autopayBit,
} from "../../../Utils/Common";
import { logApiFail, logSSMRequest, logSSMResponse } from "../../../Utils/rollbar";
import { confirmAlertShow, isValidPayment, makePayment } from "../../../Utils/paygateway";
import { updateTransactionStatus, TRANSACTION_STATUS, getOrCreateOrderId } from "../../../Utils/transactionStatus";
import ProcessPaymentRetry from "../../../Utils/ProcessPaymentRetry";
import ConfirmPaymentPlaceholder from "../../../shared/ConfirmPaymentPlaceholder";
import { addGateAccessTimeZone, addTenantNotes } from "../../../Utils/data";

class ConfirmPayment extends ProcessPaymentRetry {
  async componentDidMount() {
    const result = getPostResult();

    if (!reservationObject()) {
      confirmAlertShow("", "Invalid session!");
      window.location.href = "/";
      return;
    }

    // Get order ID - use from result if available, otherwise from session
    const orderId = result.orderId || getOrCreateOrderId('new_tenant_order_id');

    // Update status to IFRAME_SUBMITTED (user submitted payment form in iframe)
    await updateTransactionStatus(orderId, TRANSACTION_STATUS.IFRAME_SUBMITTED, {
      step: 'iframe_submitted',
      order_id: result.orderId,
    });

    const isValid = isValidPayment(
      result,
      async () => {
        // Update status to FAILED if payment was cancelled
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'payment_cancelled',
        });
        this.props.history.push("/lease-payment?retry=true");
      },
      async () => {
        // Update status to FAILED if payment validation failed
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'payment_validation_failed',
          error: result.responseDescription,
        });
        this.props.history.push("/lease-payment?retry=true");
      }
    );

    if (!isValid) {
      sessionStorage.setItem("payment_failure_notes", result.responseDescription);
      return;
    }

    const requestBody = {
      location_code: locationCode(),
      orderId: result.orderId || orderId,
    };

    makePayment(
      requestBody,
      async (err) => {
        // Update status to FAILED
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'make_payment_failed',
          error: err,
        });
        window.location.href = "/lease-payment?retry=true";
      },
      (responseData) => {
        this.finishRental(responseData);
      }
    );
  }

  finishRental = async(transactionData) => {
    const reservationDetails = reservationObject();
    const unitData = reservedUnitObject();
    const feeFor24Hours = get24HourFee();

    const startDate = moment().format("L");
    const endDate = moment().add(29, "days").format("L");

    const requestBody = {
      location_code: locationCode(),
      tenant_id: reservationDetails.tenantId,
      gate_code: getGateCode(),
      unit_id: unitData.uId,
      insurance_id: insuranceId(),
      insurance_status_id: getInsuranceStatus(),
      discount_id: finalDiscountId(),
      start_date: startDate,
      end_date: endDate,
      amount: parseFloat(amountDue()).toFixed(2),
      payment_method: "CC",
      cc_type: cardType(transactionData.CARDBRAND[0]),
      cc_no: transactionData.MASKEDCARDNUMBER[0],
      cc_expiration_date: `${transactionData.EXPMONTH[0]}/${2000 + transactionData.EXPYEAR[0] * 1}`,
      cc_address: transactionData.ADDRESS1[0].slice(0, 50),
      cc_postal_code: transactionData.BILLINGPOSTALCODE[0],
      cc_name: transactionData.CUSTOMERNAME[0],
      transaction_id: transactionData.TRANSACTIONID[0],
      cc_response_code: transactionData.RESPONSECODE[0],
      res_code: reservationDetails.reservationCode,
      dbl_reservation_paid_fee: "0.00",
      autopay_bit: autopayBit(),
      order_id: transactionData.ORDERID[0],
      token: transactionData.ALIAS[0],
      doc_ref_number: "",
      doc_base64_format: localStorage.getItem("signed_lease_pdf") || "",
    };

    const has24HourFee = feeFor24Hours && feeFor24Hours.id && feeFor24Hours.amount > 0;

    if (has24HourFee) {
      requestBody.fee_id = feeFor24Hours.id
      requestBody.fee_amount = feeFor24Hours.amount
    }

    // Get order ID for status tracking
    const orderId = requestBody.order_id || getOrCreateOrderId('new_tenant_order_id');

    // Update status to CONVERT_RESERVATION_STARTED
    await updateTransactionStatus(orderId, TRANSACTION_STATUS.CONVERT_RESERVATION_STARTED, {
      step: 'convert_reservation_started',
    });

    logSSMRequest("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", requestBody);

    axios
      .post("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", JSON.stringify(requestBody), {
        headers: { "Content-Type": "application/json" },
      })
      .then(async (response) => {
        logSSMResponse("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", response.data, true);
        let data = new XMLParser().parseFromString(response.data);

        const messageKey = has24HourFee ? "ConvertReservationtoRentalAndSendLeaseAgreement_V2Message" : "ConvertReservationToMoveInMessage"
        const resultMsg = data.getElementsByTagName(messageKey)[0].value;

        if (resultMsg === "Successful") {
          const leaseNumberKey = has24HourFee ? "ConvertReservationtoRentalAndSendLeaseAgreement_V2LeaseNumber" : "ConvertReservationToMoveInLeaseNumber"
          const leaseNumber = data.getElementsByTagName(leaseNumberKey)[0].value;
          sessionStorage.setItem("created_lease_number", leaseNumber);

          // Update status to CONVERT_RESERVATION_FINISHED and then COMPLETED
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.CONVERT_RESERVATION_FINISHED, {
            step: 'convert_reservation_success',
            lease_number: leaseNumber,
          });
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.COMPLETED, {
            step: 'payment_flow_completed',
            lease_number: leaseNumber,
          });

          if (sessionStorage.getItem("payment_failure_notes")) {
            addTenantNotes(
              locationCode(),
              reservationDetails.tenantId,
              "Kiosk Payment Failure:" + sessionStorage.getItem("payment_failure_notes")
            );
          }

          if (has24HourFee) {
            addGateAccessTimeZone(locationCode(), leaseNumber);
          }
          finishNewTenantNote(reservationObject().tenantId, "Credit Card");
          this.props.history.push("/movein-done");
        } else {
          // Update status to FAILED
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
            step: 'convert_reservation_failed',
            error: resultMsg,
          });
          throw data;
        }
      })
      .catch(async (e) => {
        logSSMResponse("/api/ssm/ConvertReservationtoRentalAndSendLeaseAgreement", e.response?.data || e.message, false);
        if (this.retryCount < 3) {
          // Retry ConvertReservationtoRentalAndSendLeaseAgreement after 3 seconds. Retry count limit: 3.
          setTimeout(() => {
            this.finishRental(transactionData);
            this.retryCount += 1;
          }, 3000);
        } else {
          // Update status to FAILED after max retries
          await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
            step: 'convert_reservation_max_retries_exceeded',
            error: e.response?.data || e.message,
            retry_count: this.retryCount,
          });
          logApiFail("ConvertReservationtoRentalAndSendLeaseAgreement", requestBody, e);
        }
      });
  };

  render() {
    return <ConfirmPaymentPlaceholder />;
  }
}

export default ConfirmPayment;
